<template>
  <main class="privacy-page">
    <h1 class="serif">{{ t('privacy.title') }}</h1>
    <p class="subtitle">{{ t('privacy.subtitle') }}</p>

    <section class="privacy-section">
      <h2 class="serif">{{ t('privacy.disclaimer.heading') }}</h2>
      <p>{{ t('privacy.disclaimer.intro') }}</p>
      <p><strong>{{ t('privacy.disclaimer.noAdvice') }}</strong> {{ t('privacy.disclaimer.noAdviceText') }}</p>
      <p><strong>{{ t('privacy.disclaimer.indicative') }}</strong> {{ t('privacy.disclaimer.indicativeText') }}</p>
      <p><strong>{{ t('privacy.disclaimer.template') }}</strong> {{ t('privacy.disclaimer.templateText') }}</p>
      <p><strong>{{ t('privacy.disclaimer.independent') }}</strong> {{ t('privacy.disclaimer.independentText') }}</p>
    </section>

    <section class="privacy-section">
      <h2 class="serif">{{ t('privacy.heading') }}</h2>
      <p><strong>{{ t('privacy.noStorage') }}</strong> {{ t('privacy.noStorageText') }}</p>
      <p><strong>{{ t('privacy.addressWoz') }}</strong> {{ t('privacy.addressWozText') }}</p>
      <p><strong>{{ t('privacy.letters') }}</strong> {{ t('privacy.lettersText') }}</p>
      <p><strong>{{ t('privacy.cookies') }}</strong> {{ t('privacy.cookiesText') }}</p>
      <div class="cookie-pref">
        <p class="cookie-pref-context">{{ t('privacy.cookieCardContext') }}</p>
        <div class="cookie-pref-toggle">
          <span class="cookie-pref-label">{{ t('privacy.cookieToggleLabel') }}</span>
          <button class="cookie-toggle" :class="{ on: cookieChoice === 'accept' }" @click="updateChoice(cookieChoice === 'accept' ? 'reject' : 'accept')" :aria-label="cookieChoice === 'accept' ? t('privacy.cookieRevoke') : t('privacy.cookieAcceptNow')">
            <span class="cookie-toggle-thumb"></span>
          </button>
        </div>
      </div>
    </section>

    <section class="privacy-section">
      <h2 class="serif">{{ t('privacy.access') }}</h2>
      <p>{{ t('privacy.accessText') }}</p>
    </section>

    <section class="privacy-section">
      <h2 class="serif">{{ t('privacy.changes') }}</h2>
      <p>{{ t('privacy.changesText') }}</p>
      <p class="last-updated">{{ t('privacy.lastUpdated') }}</p>
    </section>
  </main>
</template>

<script setup>
import { ref } from 'vue'
import { useLocale } from '../composables/useLocale'

const { t } = useLocale()

const COOKIE_KEY = 'mr-cookie-choice'

function readChoice() {
  try { return localStorage.getItem(COOKIE_KEY) } catch { return null }
}

const cookieChoice = ref(readChoice())

function updateChoice(choice) {
  try { localStorage.setItem(COOKIE_KEY, choice) } catch { /* */ }
  cookieChoice.value = choice
  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: choice === 'accept' ? 'granted' : 'denied',
    })
  }
}
</script>

<style scoped>
.privacy-page {
  flex: 1;
  max-width: 1120px;
  margin: 0 auto;
  padding: 60px 32px 80px;
  width: 100%;
}
@media (max-width: 768px) {
  .privacy-page { padding: 40px 20px 48px; }
}
.privacy-page h1 {
  font-size: clamp(28px, 5vw, 40px);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.15;
  margin-bottom: 8px;
}
.subtitle {
  font-size: 17px;
  color: var(--ink-soft);
  margin-bottom: 48px;
}
.privacy-section {
  margin-bottom: 40px;
}
.privacy-section:last-child {
  margin-bottom: 0;
}
.privacy-section h2 {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--line-soft);
}
.privacy-section p {
  margin-bottom: 16px;
  color: var(--ink-soft);
  line-height: 1.7;
}
.privacy-section p:last-child {
  margin-bottom: 0;
}
.privacy-section strong {
  color: var(--ink);
  font-weight: 700;
}
.cookie-pref {
  background: var(--bg-warm);
  border: 1px solid var(--line-soft);
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 16px;
}
.cookie-pref-context {
  flex: 1;
  font-size: 13px;
  color: var(--ink-soft);
  line-height: 1.5;
  margin: 0 !important;
  min-width: 0;
}
.cookie-pref-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.cookie-pref-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
}
@media (max-width: 600px) {
  .cookie-pref { flex-direction: column; align-items: flex-start; }
  .cookie-pref-toggle { align-self: flex-end; }
}
.cookie-toggle {
  flex-shrink: 0;
  width: 44px;
  height: 26px;
  border-radius: 13px;
  border: none;
  background: var(--ink-muted);
  cursor: pointer;
  position: relative;
  transition: background 0.25s;
  padding: 0;
}
.cookie-toggle.on {
  background: #22c55e;
}
.cookie-toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.25s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}
.cookie-toggle.on .cookie-toggle-thumb {
  transform: translateX(18px);
}
.last-updated {
  font-size: 14px;
  color: var(--ink-muted);
  font-style: italic;
  margin-top: 24px;
}
</style>
